import { deviceBootstrapRequestBody, deviceBootstrapStorageKey } from "./swiftClientCore";

export type DeviceBootstrapRecord = {
  deviceId: string;
  platform: string;
  channel: string;
  version: string;
  attestationStatus?: string;
  attestationProvider?: string;
  registeredAt: string;
};

export type AppleAppAttestBootstrap = {
  key_id?: string;
  team_id?: string;
  bundle_id?: string;
  environment?: "development" | "production";
  challenge?: string;
};

const storageKey = deviceBootstrapStorageKey;

export function loadBootstrapRecord(): DeviceBootstrapRecord | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as DeviceBootstrapRecord;
  } catch {
    return null;
  }
}

export async function bootstrapDevice(options: {
  baseUrl?: string;
  platform?: string;
  channel?: string;
  version?: string;
  appAttest?: AppleAppAttestBootstrap;
} = {}): Promise<DeviceBootstrapRecord | null> {
  const baseUrl = options.baseUrl ?? "https://api.key.store";
  const platform = options.platform ?? "web";
  const channel = options.channel ?? import.meta.env.MODE ?? "web";
  const version = options.version ?? import.meta.env.VITE_APP_VERSION ?? "0.0.0";

  const existing = loadBootstrapRecord();
  if (existing && existing.platform === platform && existing.channel === channel) {
    if (existing.version !== version) {
      const updated = { ...existing, version };
      storeRecord(updated);
      void patchDevice(baseUrl, updated);
      return updated;
    }
    return existing;
  }

  const response = await fetch(`${baseUrl.replace(/\/$/, "")}/v1/devices`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: await deviceBootstrapRequestBody(platform, channel, version, options.appAttest),
  });

  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as {
    device_id?: string;
    deviceId?: string;
    attestation_status?: string;
    attestation_provider?: string;
  };
  const deviceId = data.device_id ?? data.deviceId;
  if (!deviceId) {
    return null;
  }

  const record: DeviceBootstrapRecord = {
    deviceId,
    platform,
    channel,
    version,
    attestationStatus: data.attestation_status,
    attestationProvider: data.attestation_provider,
    registeredAt: new Date().toISOString(),
  };
  storeRecord(record);
  return record;
}

async function patchDevice(baseUrl: string, record: DeviceBootstrapRecord) {
  try {
    await fetch(`${baseUrl.replace(/\/$/, "")}/v1/devices/${record.deviceId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ version: record.version }),
    });
  } catch {
    return;
  }
}

function storeRecord(record: DeviceBootstrapRecord) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(record));
  } catch {
    return;
  }
}
